import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import WikiHeader from '../components/WikiHeader';
import { DataGrid } from '@/lib/dreamstep-sdk/datagrid';
import type { ColumnConfig, GridConfig } from '@/lib/dreamstep-sdk/datagrid';
import { dsWikiService } from '../services';
import type { Department as ApiDepartment, ArticleCategory as ApiTag, ArticleType as ApiArticleType } from '../services/types';
import { ListSkeleton } from '@/components/base/Skeleton';

interface Article {
  id: number;
  title: string;
  summary: string;
  views: number;
  lastUpdated: string;
  author: string;
  status: 'published' | 'under_curation' | 'draft';
  tags: string[];
  category: 'article' | 'circular' | 'go';
  categoryNumber?: string; // For circulars and GOs
}

type FilterType = 'all' | 'week' | 'month' | 'sixMonths';
type StatusFilter = 'all' | 'published' | 'under_curation' | 'draft';
type CategoryFilter = 'all' | string; // 'all' or article type ID
type RankingTab = 'contributors' | 'articles';
type ViewMode = 'articles' | 'favorites' | 'stats-department' | 'stats-location' | 'stats-tags';

interface FavoriteItem {
  id: number;
  title: string;
  category: 'article' | 'circular' | 'go';
  categoryNumber?: string;
  summary: string;
  author: string;
  lastUpdated: string;
  views: number;
  department: string;
}

export default function DepartmentDetail() {
  const { departmentId } = useParams<{ departmentId: string }>();
  const [openFilterPanelFor, setOpenFilterPanelFor] = useState<'workspace' | 'department' | 'location' | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('articles');

  // Configuration modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configType, setConfigType] = useState<'department' | 'location' | 'tag' | null>(null);
  const [selectedConfigItem, setSelectedConfigItem] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');

  // Create new modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState<'department' | 'location' | 'tag' | 'subtag' | null>(null);
  const [newItemName, setNewItemName] = useState('');
  const [parentTagForSubtag, setParentTagForSubtag] = useState<string>('');
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [myArticlesFilter, setMyArticlesFilter] = useState<'all' | 'published' | 'under_curation' | 'draft' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [rankingTab, setRankingTab] = useState<RankingTab>('contributors');
  const [showAllContributors, setShowAllContributors] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [tagEditMode, setTagEditMode] = useState(false);
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editTagName, setEditTagName] = useState('');
  const [showAssignTagsModal, setShowAssignTagsModal] = useState(false);
  const [selectedArticleForTags, setSelectedArticleForTags] = useState<number | null>(null);
  const [selectedSubTags, setSelectedSubTags] = useState<string[]>([]);
  const [showSubTagInput, setShowSubTagInput] = useState<string | null>(null);
  const [newSubTagName, setNewSubTagName] = useState('');
  const [editingSubTag, setEditingSubTag] = useState<{parent: string, name: string} | null>(null);
  const [editSubTagName, setEditSubTagName] = useState('');

  // API data states
  const [apiDepartments, setApiDepartments] = useState<ApiDepartment[]>([]);
  const [apiCategories, setApiCategories] = useState<ApiTag[]>([]);
  const [apiArticleTypes, setApiArticleTypes] = useState<ApiArticleType[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  // Article type dropdown state
  const [showArticleTypeDropdown, setShowArticleTypeDropdown] = useState(false);
  const articleTypeDropdownRef = useRef<HTMLDivElement>(null);

  // Dashboard data states
  const [myArticlesData, setMyArticlesData] = useState<any[]>([]);
  const [topContributorsData, setTopContributorsData] = useState<any[]>([]);
  const [departmentCuratorsData, setDepartmentCuratorsData] = useState<any[]>([]);
  const [topArticlesData, setTopArticlesData] = useState<any[]>([]);
  const [dashboardStats, setDashboardStats] = useState<any>({});

  // Config modal curators data
  const [configCurators, setConfigCurators] = useState<any[]>([]);
  const [configCuratorsLoading, setConfigCuratorsLoading] = useState(false);

  // Close article type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (articleTypeDropdownRef.current && !articleTypeDropdownRef.current.contains(event.target as Node)) {
        setShowArticleTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch data from API on mount
  useEffect(() => {
    const fetchData = async () => {
      setApiLoading(true);
      setApiError(null);
      try {
        const [departmentsRes, categoriesRes, articleTypesRes] = await Promise.all([
          dsWikiService.getActiveDepartments(),
          dsWikiService.getActiveArticleCategories(),
          dsWikiService.getActiveArticleTypes()
        ]);
        setApiDepartments(departmentsRes.data || []);
        setApiCategories(categoriesRes.data || []);
        setApiArticleTypes(articleTypesRes.data || []);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setApiError(err.message || 'Failed to fetch data');
      } finally {
        setApiLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch dashboard data from API on mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      console.log('Fetching dashboard data...');
      try {
        const [myArticlesRes, topContributorsRes, curatorsRes, topArticlesRes, statsRes] = await Promise.all([
          dsWikiService.getMyArticles(),
          dsWikiService.getTopContributors(5),
          dsWikiService.getDepartmentCurators(departmentId ? parseInt(departmentId) : undefined),
          dsWikiService.getTopArticles(5),
          dsWikiService.getDashboardStats()
        ]);

        console.log('Dashboard API responses:', {
          myArticles: myArticlesRes,
          topContributors: topContributorsRes,
          curators: curatorsRes,
          topArticles: topArticlesRes,
          stats: statsRes
        });

        setMyArticlesData(myArticlesRes.data || []);
        setTopContributorsData(topContributorsRes.data || []);
        setDepartmentCuratorsData(curatorsRes.data || []);
        setTopArticlesData(topArticlesRes.data || []);
        setDashboardStats(statsRes.data || {});
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
      }
    };

    fetchDashboardData();
  }, [departmentId]);

  // Contributors and editors - will be populated from API when available
  const contributors: { name: string; role: string; avatar: string }[] = [];
  const editors: { name: string; role: string; avatar: string }[] = [];

  // Tags with sub-tags - will be populated from API
  interface SubTag {
    name: string;
    count: number;
  }

  interface Tag {
    name: string;
    count: number;
    subTags?: SubTag[];
  }

  // Transform API categories to tags format
  const tags: Tag[] = apiCategories.map(cat => ({
    name: cat.ctgry_nm,
    count: 0, // Will be populated when article count API is available
    subTags: []
  }));

  // Top articles data - from API
  const topArticles = topArticlesData.map(a => ({
    id: a.dswiki_artcl_id,
    title: a.artcl_ttl,
    views: a.vw_ct || 0
  }));

  // My articles stats - calculated from API data
  const myArticlesStats = {
    total: myArticlesData.length,
    published: myArticlesData.filter(a => a.sts_nm === 'Published').length,
    underReview: myArticlesData.filter(a => a.sts_nm === 'Under Review' || a.sts_nm === 'Pending').length,
    draft: myArticlesData.filter(a => a.sts_nm === 'Draft').length
  };

  // Top contributors - from API
  const topContributorsList = topContributorsData.map(c => ({
    name: c.contributor_nm || 'Unknown',
    articles: c.article_ct || 0,
    avatar: (c.contributor_nm || 'U').charAt(0).toUpperCase()
  }));

  // Department curators - from API
  const departmentCuratorsList = departmentCuratorsData.map(c => ({
    name: c.crtr_nm || c.user_nm || 'Unknown',
    role: c.is_snr_in ? 'Senior Curator' : 'Curator',
    articles: (c.rvw_cmplt_ct || 0) + (c.aprv_ct || 0),
    avatar: (c.crtr_nm || c.user_nm || 'U').charAt(0).toUpperCase()
  }));

  const toggleTag = (tagName: string) => {
    setSelectedTags(prev =>
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const handleCreateTag = () => {
    if (newTagName.trim()) {
      // In real app, this would call an API
      console.log('Creating tag:', newTagName.trim());
      alert(`Tag "${newTagName.trim()}" created successfully!`);
      setNewTagName('');
      setShowNewTagInput(false);
    }
  };

  const handleEditTag = (oldName: string) => {
    if (editTagName.trim() && editTagName !== oldName) {
      // In real app, this would call an API
      console.log('Editing tag:', oldName, '->', editTagName.trim());
      alert(`Tag "${oldName}" renamed to "${editTagName.trim()}"!`);
      setEditingTag(null);
      setEditTagName('');
    }
  };

  const handleDeleteTag = (tagName: string) => {
    if (confirm(`Are you sure you want to delete the tag "${tagName}"?`)) {
      // In real app, this would call an API
      console.log('Deleting tag:', tagName);
      alert(`Tag "${tagName}" deleted!`);
    }
  };

  // Sub-tag handlers
  const toggleSubTag = (subTagName: string) => {
    setSelectedSubTags(prev =>
      prev.includes(subTagName)
        ? prev.filter(t => t !== subTagName)
        : [...prev, subTagName]
    );
  };

  const handleCreateSubTag = (parentTag: string) => {
    if (newSubTagName.trim()) {
      console.log('Creating sub-tag:', newSubTagName.trim(), 'under', parentTag);
      alert(`Sub-tag "${newSubTagName.trim()}" created under "${parentTag}"!`);
      setNewSubTagName('');
      setShowSubTagInput(null);
    }
  };

  const handleEditSubTag = (parentTag: string, oldName: string) => {
    if (editSubTagName.trim() && editSubTagName !== oldName) {
      console.log('Editing sub-tag:', oldName, '->', editSubTagName.trim(), 'in', parentTag);
      alert(`Sub-tag "${oldName}" renamed to "${editSubTagName.trim()}"!`);
      setEditingSubTag(null);
      setEditSubTagName('');
    }
  };

  const handleDeleteSubTag = (parentTag: string, subTagName: string) => {
    if (confirm(`Are you sure you want to delete the sub-tag "${subTagName}"?`)) {
      console.log('Deleting sub-tag:', subTagName, 'from', parentTag);
      alert(`Sub-tag "${subTagName}" deleted!`);
    }
  };

  // Get selected parent tags that have sub-tags
  const selectedParentTagsWithSubTags = tags.filter(
    tag => selectedTags.includes(tag.name) && tag.subTags && tag.subTags.length > 0
  );

  const openAssignTagsModal = (articleId: number) => {
    setSelectedArticleForTags(articleId);
    setShowAssignTagsModal(true);
  };

  const handleAssignTag = (tagName: string) => {
    // In real app, this would call an API
    console.log('Assigning tag:', tagName, 'to article:', selectedArticleForTags);
  };

  const clearAllFilters = () => {
    setSelectedFilter('all');
    setStatusFilter('all');
    setCategoryFilter('all');
    setSearchQuery('');
    setSelectedTags([]);
    setSelectedSubTags([]);
    setMyArticlesFilter(null);
  };

  const hasActiveFilters = selectedFilter !== 'all' || statusFilter !== 'all' || categoryFilter !== 'all' || searchQuery || selectedTags.length > 0 || selectedSubTags.length > 0 || myArticlesFilter !== null;

  // Check if we're on the home route (no departmentId)
  const isHomeRoute = !departmentId;

  // Get department from API data or show default
  const apiDept = apiDepartments.find(d => String(d.dswiki_dpt_id) === departmentId);
  const department = isHomeRoute ? {
    id: 'all',
    name: 'All Departments',
    totalArticles: 0, // Will be populated from API when article count is available
    thisWeek: 0,
    thisMonth: 0,
    lastSixMonths: 0
  } : {
    id: departmentId,
    name: apiDept?.dpt_nm || 'Department',
    totalArticles: 0,
    thisWeek: 0,
    thisMonth: 0,
    lastSixMonths: 0
  };

  // Handler to open filter panel with department dropdown focused
  const handleDepartmentNameClick = () => {
    setOpenFilterPanelFor('department');
  };

  // Reset the trigger after the panel opens
  const handleFilterPanelOpenChange = (isOpen: boolean) => {
    if (isOpen && openFilterPanelFor) {
      // Reset the trigger after a short delay
      setTimeout(() => setOpenFilterPanelFor(null), 200);
    }
  };

  // Handler for opening configuration modal
  const openConfigModal = async (type: 'department' | 'location' | 'tag', item: any) => {
    setConfigType(type);
    setSelectedConfigItem(item);
    setEditName(item.department || item.location || item.tag || '');
    setShowConfigModal(true);
    setIsEditing(false);
    setConfigCurators([]);

    // Fetch curators for department
    if (type === 'department' && item.department) {
      // Find the department ID from apiDepartments
      const dept = apiDepartments.find(d => d.dpt_nm === item.department);
      if (dept?.dswiki_dpt_id) {
        setConfigCuratorsLoading(true);
        try {
          const curatorsRes = await dsWikiService.getCuratorsByDepartment(dept.dswiki_dpt_id);
          setConfigCurators(curatorsRes.data || []);
        } catch (err) {
          console.error('Error fetching curators:', err);
          setConfigCurators([]);
        } finally {
          setConfigCuratorsLoading(false);
        }
      }
    }
  };

  // Handler for saving edit
  const handleSaveEdit = () => {
    // In real app, would save to backend
    console.log('Saving:', configType, editName);
    setIsEditing(false);
  };

  // Handler for opening create modal
  const openCreateModal = (type: 'department' | 'location') => {
    setCreateType(type);
    setNewItemName('');
    setShowCreateModal(true);
  };

  // Handler for creating new item
  const handleCreateItem = async () => {
    if (!newItemName.trim()) return;

    try {
      if (createType === 'department') {
        await dsWikiService.saveDepartment({ dpt_nm: newItemName.trim() });
        // Refresh departments list
        const deptRes = await dsWikiService.getActiveDepartments();
        setApiDepartments(deptRes.data || []);
      } else if (createType === 'location') {
        await dsWikiService.saveLocation({ lctn_nm: newItemName.trim() });
      } else if (createType === 'tag') {
        await dsWikiService.saveTag({ tag_nm: newItemName.trim() });
        // Refresh categories/tags list
        const catRes = await dsWikiService.getActiveArticleCategories();
        setApiCategories(catRes.data || []);
      }

      setShowCreateModal(false);
      setNewItemName('');
    } catch (err: any) {
      console.error('Error creating item:', err);
      alert(err.message || 'Failed to create item');
    }
  };

  // Stats data for different views - will be populated from API when stats endpoint is available
  // Transform API departments to stats format
  const statsByDepartment = apiDepartments.map(dept => ({
    department: dept.dpt_nm,
    total: 0,
    published: 0,
    underReview: 0,
    draft: 0,
    views: 0
  }));

  // Location stats - will be populated from API when location/workspace endpoint provides this data
  const statsByLocation: { location: string; total: number; published: number; underReview: number; draft: number; views: number }[] = [];

  // Tag stats - will be populated from API when tag statistics endpoint is available
  const statsByTags = apiCategories.map(cat => ({
    tag: cat.ctgry_nm,
    subTag: '',
    total: 0,
    published: 0,
    underReview: 0,
    draft: 0,
    views: 0,
    isParent: true
  }));

  // Column configurations for stats grids
  const departmentStatsColumns: ColumnConfig[] = [
    { columnName: 'Department', field: 'department', columnType: 'text', width: '180px' },
    { columnName: 'Total', field: 'total', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Published', field: 'published', columnType: 'number', width: '100px', alignment: 'center' },
    { columnName: 'Under Review', field: 'underReview', columnType: 'number', width: '110px', alignment: 'center' },
    { columnName: 'Draft', field: 'draft', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Total Views', field: 'views', columnType: 'number', width: '110px', alignment: 'right' },
    {
      columnName: 'Actions',
      field: 'actions',
      columnType: 'template',
      width: '100px',
      alignment: 'center',
      cellRenderer: (row: any) => (
        <button
          onClick={() => openConfigModal('department', row)}
          className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors flex items-center gap-1 mx-auto"
        >
          <i className="ri-settings-3-line"></i>
          Configure
        </button>
      )
    }
  ];

  const locationStatsColumns: ColumnConfig[] = [
    { columnName: 'Location', field: 'location', columnType: 'text', width: '180px' },
    { columnName: 'Total', field: 'total', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Published', field: 'published', columnType: 'number', width: '100px', alignment: 'center' },
    { columnName: 'Under Review', field: 'underReview', columnType: 'number', width: '110px', alignment: 'center' },
    { columnName: 'Draft', field: 'draft', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Total Views', field: 'views', columnType: 'number', width: '110px', alignment: 'right' },
    {
      columnName: 'Actions',
      field: 'actions',
      columnType: 'template',
      width: '100px',
      alignment: 'center',
      cellRenderer: (row: any) => (
        <button
          onClick={() => openConfigModal('location', row)}
          className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 transition-colors flex items-center gap-1 mx-auto"
        >
          <i className="ri-settings-3-line"></i>
          Configure
        </button>
      )
    }
  ];

  const tagStatsColumns: ColumnConfig[] = [
    {
      columnName: 'Tag / Sub-Tag',
      field: 'tag',
      columnType: 'template',
      width: '220px',
      cellRenderer: (row: any) => (
        <div className={`flex items-center ${row.isParent ? 'font-semibold text-gray-900' : 'pl-6 text-gray-600'}`}>
          {row.isParent ? (
            <>
              <i className="ri-price-tag-3-fill mr-2 text-purple-600"></i>
              {row.tag}
            </>
          ) : (
            <>
              <i className="ri-corner-down-right-line mr-2 text-gray-400"></i>
              <i className="ri-price-tag-3-line mr-1.5 text-purple-400 text-sm"></i>
              <span className="text-gray-700">{row.subTag}</span>
            </>
          )}
        </div>
      )
    },
    { columnName: 'Total', field: 'total', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Published', field: 'published', columnType: 'number', width: '100px', alignment: 'center' },
    { columnName: 'Under Review', field: 'underReview', columnType: 'number', width: '110px', alignment: 'center' },
    { columnName: 'Draft', field: 'draft', columnType: 'number', width: '80px', alignment: 'center' },
    { columnName: 'Total Views', field: 'views', columnType: 'number', width: '110px', alignment: 'right' },
    {
      columnName: 'Actions',
      field: 'actions',
      columnType: 'template',
      width: '100px',
      alignment: 'center',
      cellRenderer: (row: any) => (
        <button
          onClick={() => openConfigModal('tag', row)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 mx-auto ${
            row.isParent
              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <i className={row.isParent ? 'ri-settings-3-line' : 'ri-edit-line'}></i>
          {row.isParent ? 'Configure' : 'Edit'}
        </button>
      )
    }
  ];

  const statsGridConfig: GridConfig = {
    pagination: false,
    enableSearchPanel: false,
    enableColumnChooser: false,
    enableColumnFilters: false,
    headerCustomization: {
      style: 'modern',
      headerBackgroundColor: '#f1f5f9',
      headerTextColor: '#334155',
      headerFontWeight: '600'
    }
  };

  // Articles data - will be populated from API when available
  // TODO: Call Articles API when available to fetch articles by time period
  const articlesThisWeek: Article[] = [];
  const articlesThisMonth: Article[] = [];
  const articlesLastSixMonths: Article[] = [];

  // All articles (for 'all' filter) - will be populated from API
  const allArticles: Article[] = [];

  // Favorites data - will be populated from API when available
  const favoriteItems: FavoriteItem[] = [];

  // Current user - should come from auth context
  // TODO: Get current user from authentication context
  const currentUser = '';

  // Filter and search articles
  const filteredArticles = useMemo(() => {
    let articles: Article[];

    switch (selectedFilter) {
      case 'week': articles = articlesThisWeek; break;
      case 'month': articles = articlesThisMonth; break;
      case 'sixMonths': articles = articlesLastSixMonths; break;
      default: articles = allArticles;
    }

    // Filter by "My Articles" - only show articles created by current user
    if (myArticlesFilter !== null) {
      articles = articles.filter(article => article.author === currentUser);
      // Further filter by status within my articles
      if (myArticlesFilter !== 'all') {
        articles = articles.filter(article => article.status === myArticlesFilter);
      }
    }

    // Filter by status (separate from My Articles filter)
    if (statusFilter !== 'all') {
      articles = articles.filter(article => article.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      articles = articles.filter(article => article.category === categoryFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      articles = articles.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.summary.toLowerCase().includes(query) ||
        article.author.toLowerCase().includes(query)
      );
    }

    return articles;
  }, [selectedFilter, statusFilter, categoryFilter, searchQuery, myArticlesFilter]);

  // Helper functions for category display
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'article': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'circular': return 'bg-green-100 text-green-700 border-green-200';
      case 'go': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'article': return 'ri-article-line';
      case 'circular': return 'ri-file-text-line';
      case 'go': return 'ri-government-line';
      default: return 'ri-file-line';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'article': return 'Article';
      case 'circular': return 'Circular';
      case 'go': return 'GO';
      default: return 'Document';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'under_curation': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published': return 'Published';
      case 'under_curation': return 'Under Review';
      case 'draft': return 'Draft';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Wiki Header with Breadcrumbs */}
      <WikiHeader
        breadcrumbs={isHomeRoute ? [
          { label: 'All Articles' }
        ] : [
          { label: 'Articles', href: '/app/ds-wiki/articles' },
          { label: department.name }
        ]}
        showFilterButton={true}
        openFilterPanelFor={openFilterPanelFor}
        onFilterPanelOpenChange={handleFilterPanelOpenChange}
      />

      <div className="px-6 py-6">
        {/* Combined Filter Bar with Search */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
          {/* Top Bar - Department Name + Time Filters + View Mode (Sleek) */}
          <div className="flex items-center border-b border-gray-200">
            {/* Department Name - Clickable to open filter panel */}
            <button
              onClick={handleDepartmentNameClick}
              className="flex items-center gap-2 px-4 py-2 border-r border-gray-200 hover:bg-blue-50 transition-colors group"
              title="Click to filter by department"
            >
              <i className="ri-folder-3-line text-blue-600"></i>
              <span className="font-semibold text-sm text-gray-900">{department.name}</span>
              <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-blue-600 transition-colors"></i>
            </button>

            {/* Time Filter Tabs - Sleeker */}
            <div className="flex-1 flex items-center">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all duration-200 border-b-2 ${
                  selectedFilter === 'all'
                    ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>Total</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  selectedFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {department.totalArticles}
                </span>
              </button>
              <div className="w-px h-5 bg-gray-200"></div>
              <button
                onClick={() => setSelectedFilter('week')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all duration-200 border-b-2 ${
                  selectedFilter === 'week'
                    ? 'border-green-500 text-green-600 bg-green-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>This Week</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  selectedFilter === 'week' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {department.thisWeek}
                </span>
              </button>
              <div className="w-px h-5 bg-gray-200"></div>
              <button
                onClick={() => setSelectedFilter('month')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all duration-200 border-b-2 ${
                  selectedFilter === 'month'
                    ? 'border-purple-500 text-purple-600 bg-purple-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>Last 30 Days</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  selectedFilter === 'month' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {department.thisMonth}
                </span>
              </button>
              <div className="w-px h-5 bg-gray-200"></div>
              <button
                onClick={() => setSelectedFilter('sixMonths')}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all duration-200 border-b-2 ${
                  selectedFilter === 'sixMonths'
                    ? 'border-orange-500 text-orange-600 bg-orange-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>Last 6 Months</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  selectedFilter === 'sixMonths' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {department.lastSixMonths}
                </span>
              </button>
            </div>

            {/* View Mode Dropdown */}
            <div className="flex items-center border-l border-gray-200 px-3">
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as ViewMode)}
                className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer appearance-none pr-8"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1rem 1rem'
                }}
              >
                <option value="articles">Articles List</option>
                <option value="favorites">⭐ My Favorites</option>
                <option value="stats-department">Stats by Department</option>
                <option value="stats-location">Stats by Location</option>
                <option value="stats-tags">Stats by Tags</option>
              </select>
            </div>
          </div>

          {/* Search Bar Row */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="relative flex-1 max-w-lg">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>

              {/* Article Type Dropdown */}
              <div className="relative ml-4" ref={articleTypeDropdownRef}>
                <button
                  onClick={() => setShowArticleTypeDropdown(!showArticleTypeDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-all shadow-sm"
                >
                  <i className="ri-file-list-3-line text-blue-600"></i>
                  <span className="text-gray-700">
                    {categoryFilter === 'all'
                      ? 'All Types'
                      : apiArticleTypes.find(t => String(t.dswiki_artcl_type_id) === categoryFilter)?.artcl_type_nm || 'All Types'}
                  </span>
                  <i className={`ri-arrow-${showArticleTypeDropdown ? 'up' : 'down'}-s-line text-gray-400 transition-transform`}></i>
                </button>

                {/* Dropdown Menu */}
                {showArticleTypeDropdown && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[9999] max-h-80 overflow-y-auto">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Filter by Type</p>
                    </div>

                    {/* All Types Option */}
                    <button
                      onClick={() => {
                        setCategoryFilter('all');
                        setShowArticleTypeDropdown(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors ${
                        categoryFilter === 'all' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-apps-line text-gray-600"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">All Types</p>
                        <p className="text-xs text-gray-500">Show all article types</p>
                      </div>
                      {categoryFilter === 'all' && <i className="ri-check-line text-blue-600"></i>}
                    </button>

                    <div className="border-t border-gray-100 my-1"></div>

                    {/* Article Type Options */}
                    {apiArticleTypes.length > 0 ? (
                      apiArticleTypes.map((articleType) => (
                        <button
                          key={articleType.dswiki_artcl_type_id}
                          onClick={() => {
                            setCategoryFilter(String(articleType.dswiki_artcl_type_id));
                            setShowArticleTypeDropdown(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 transition-colors ${
                            categoryFilter === String(articleType.dswiki_artcl_type_id) ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-text-line text-blue-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{articleType.artcl_type_nm}</p>
                            {articleType.dscn_tx && (
                              <p className="text-xs text-gray-500 line-clamp-1">{articleType.dscn_tx}</p>
                            )}
                          </div>
                          {categoryFilter === String(articleType.dswiki_artcl_type_id) && (
                            <i className="ri-check-line text-blue-600"></i>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <i className="ri-file-list-3-line text-3xl text-gray-300 mb-2"></i>
                        <p className="text-sm text-gray-500">No article types available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 ml-4">
                <div className="text-sm text-gray-500">
                  {filteredArticles.length} item{filteredArticles.length !== 1 ? 's' : ''} found
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                  >
                    <i className="ri-close-circle-line"></i>
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Active Filters Row */}
          {hasActiveFilters && (
            <div className="px-4 py-2 bg-gray-50 flex items-center flex-wrap gap-2">
              <span className="text-xs text-gray-500 font-medium">Active:</span>
              {myArticlesFilter !== null && (
                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  <i className="ri-user-line mr-1"></i>
                  My Articles{myArticlesFilter !== 'all' ? `: ${myArticlesFilter === 'published' ? 'Published' : myArticlesFilter === 'under_curation' ? 'Under Review' : 'Draft'}` : ''}
                  <button onClick={() => setMyArticlesFilter(null)} className="ml-1 hover:text-indigo-900">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {selectedFilter !== 'all' && (
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  {selectedFilter === 'week' ? 'This Week' : selectedFilter === 'month' ? 'Last 30 Days' : 'Last 6 Months'}
                  <button onClick={() => setSelectedFilter('all')} className="ml-1 hover:text-blue-900">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {statusFilter !== 'all' && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center ${
                  statusFilter === 'published' ? 'bg-green-100 text-green-700' :
                  statusFilter === 'under_curation' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-gray-200 text-gray-700'
                }`}>
                  {statusFilter === 'published' ? 'Published' : statusFilter === 'under_curation' ? 'Under Review' : 'Draft'}
                  <button onClick={() => setStatusFilter('all')} className="ml-1 hover:opacity-70">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {categoryFilter !== 'all' && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center border ${getCategoryColor(categoryFilter)}`}>
                  <i className={`${getCategoryIcon(categoryFilter)} mr-1`}></i>
                  {getCategoryLabel(categoryFilter)}
                  <button onClick={() => setCategoryFilter('all')} className="ml-1 hover:opacity-70">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {searchQuery && (
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-purple-900">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              )}
              {selectedTags.map(tag => (
                <span key={tag} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  #{tag}
                  <button onClick={() => toggleTag(tag)} className="ml-1 hover:text-green-900">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
              {selectedSubTags.map(subTag => (
                <span key={subTag} className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  <i className="ri-git-branch-line mr-1 text-[10px]"></i>
                  {subTag}
                  <button onClick={() => toggleSubTag(subTag)} className="ml-1 hover:text-blue-900">
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Tags Filter Row */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700 flex items-center">
                <i className="ri-price-tag-3-line mr-2 text-green-600"></i>
                Filter by Tags
              </span>
              <div className="flex items-center space-x-2">
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear filters
                  </button>
                )}
                <button
                  onClick={() => setShowNewTagInput(true)}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center"
                  title="Create new tag"
                >
                  <i className="ri-add-circle-line mr-1"></i>
                  New Tag
                </button>
                <button
                  onClick={() => setTagEditMode(!tagEditMode)}
                  className={`text-xs flex items-center ${tagEditMode ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Manage tags"
                >
                  <i className={`${tagEditMode ? 'ri-check-line' : 'ri-settings-3-line'} mr-1`}></i>
                  {tagEditMode ? 'Done' : 'Manage'}
                </button>
              </div>
            </div>

            {/* New Tag Input */}
            {showNewTagInput && (
              <div className="flex items-center gap-2 mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <i className="ri-price-tag-3-line text-green-600"></i>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Enter tag name..."
                  className="flex-1 px-3 py-1.5 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateTag();
                    if (e.key === 'Escape') {
                      setShowNewTagInput(false);
                      setNewTagName('');
                    }
                  }}
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowNewTagInput(false);
                    setNewTagName('');
                  }}
                  className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            <div className="flex items-center flex-wrap gap-2">
              {(showAllTags ? tags : tags.slice(0, 10)).map(tag => (
                <div key={tag.name} className="relative group">
                  {editingTag === tag.name ? (
                    <div className="flex items-center gap-1 bg-blue-50 rounded-full px-2 py-1 border border-blue-300">
                      <input
                        type="text"
                        value={editTagName}
                        onChange={(e) => setEditTagName(e.target.value)}
                        className="w-20 px-2 py-0.5 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditTag(tag.name);
                          if (e.key === 'Escape') {
                            setEditingTag(null);
                            setEditTagName('');
                          }
                        }}
                      />
                      <button
                        onClick={() => handleEditTag(tag.name)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <i className="ri-check-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => {
                          setEditingTag(null);
                          setEditTagName('');
                        }}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => !tagEditMode && toggleTag(tag.name)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border-2 ${
                        selectedTags.includes(tag.name)
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-gray-100 text-gray-700 border-transparent hover:bg-green-100 hover:border-green-400 hover:text-green-700'
                      } ${tagEditMode ? 'pr-20' : ''}`}
                    >
                      #{tag.name}
                      <span className={`${selectedTags.includes(tag.name) ? 'text-green-100' : 'text-gray-400'}`}>
                        ({tag.count})
                      </span>
                      {tag.subTags && tag.subTags.length > 0 && (
                        <i className={`ri-arrow-down-s-line text-xs ${selectedTags.includes(tag.name) ? 'text-green-100' : 'text-gray-400'}`}></i>
                      )}
                    </button>
                  )}
                  {/* Edit/Delete/Add Sub-tag buttons in edit mode */}
                  {tagEditMode && editingTag !== tag.name && (
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center space-x-1">
                      {tag.subTags !== undefined && (
                        <button
                          onClick={() => setShowSubTagInput(tag.name)}
                          className="w-5 h-5 rounded bg-green-100 text-green-600 hover:bg-green-200 flex items-center justify-center"
                          title="Add sub-tag"
                        >
                          <i className="ri-add-line text-xs"></i>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingTag(tag.name);
                          setEditTagName(tag.name);
                        }}
                        className="w-5 h-5 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center"
                        title="Edit tag"
                      >
                        <i className="ri-pencil-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => handleDeleteTag(tag.name)}
                        className="w-5 h-5 rounded bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center"
                        title="Delete tag"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {tags.length > 10 && (
                <button
                  onClick={() => setShowAllTags(!showAllTags)}
                  className="px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"
                >
                  {showAllTags ? (
                    <>
                      <i className="ri-arrow-up-s-line mr-1"></i>
                      Show less
                    </>
                  ) : (
                    <>
                      +{tags.length - 10} more
                      <i className="ri-arrow-down-s-line ml-1"></i>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Sub-tags Section - Shows when parent tag is selected */}
            {selectedParentTagsWithSubTags.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-git-branch-line text-blue-600"></i>
                  <span className="text-sm font-medium text-gray-700">Sub-tags</span>
                  <span className="text-xs text-gray-500">(Filter further by selecting sub-tags)</span>
                </div>
                {selectedParentTagsWithSubTags.map(parentTag => (
                  <div key={parentTag.name} className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">
                        #{parentTag.name}
                      </span>
                      {tagEditMode && (
                        <button
                          onClick={() => setShowSubTagInput(parentTag.name)}
                          className="text-xs text-green-600 hover:text-green-700 flex items-center"
                        >
                          <i className="ri-add-circle-line mr-1"></i>
                          Add Sub-tag
                        </button>
                      )}
                    </div>

                    {/* Add Sub-tag Input */}
                    {showSubTagInput === parentTag.name && (
                      <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <i className="ri-price-tag-3-line text-blue-600 text-sm"></i>
                        <input
                          type="text"
                          value={newSubTagName}
                          onChange={(e) => setNewSubTagName(e.target.value)}
                          placeholder="Enter sub-tag name..."
                          className="flex-1 px-2 py-1 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateSubTag(parentTag.name);
                            if (e.key === 'Escape') {
                              setShowSubTagInput(null);
                              setNewSubTagName('');
                            }
                          }}
                        />
                        <button
                          onClick={() => handleCreateSubTag(parentTag.name)}
                          disabled={!newSubTagName.trim()}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add
                        </button>
                        <button
                          onClick={() => {
                            setShowSubTagInput(null);
                            setNewSubTagName('');
                          }}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    )}

                    <div className="flex items-center flex-wrap gap-2 pl-4">
                      {parentTag.subTags?.map(subTag => (
                        <div key={subTag.name} className="relative group">
                          {editingSubTag?.parent === parentTag.name && editingSubTag?.name === subTag.name ? (
                            <div className="flex items-center gap-1 bg-blue-50 rounded-full px-2 py-1 border border-blue-300">
                              <input
                                type="text"
                                value={editSubTagName}
                                onChange={(e) => setEditSubTagName(e.target.value)}
                                className="w-20 px-2 py-0.5 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleEditSubTag(parentTag.name, subTag.name);
                                  if (e.key === 'Escape') {
                                    setEditingSubTag(null);
                                    setEditSubTagName('');
                                  }
                                }}
                              />
                              <button
                                onClick={() => handleEditSubTag(parentTag.name, subTag.name)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <i className="ri-check-line text-xs"></i>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSubTag(null);
                                  setEditSubTagName('');
                                }}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => !tagEditMode && toggleSubTag(subTag.name)}
                              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 border ${
                                selectedSubTags.includes(subTag.name)
                                  ? 'bg-blue-500 text-white border-blue-500'
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700'
                              } ${tagEditMode ? 'pr-12' : ''}`}
                            >
                              {subTag.name}
                              <span className={`ml-1 ${selectedSubTags.includes(subTag.name) ? 'text-blue-100' : 'text-gray-400'}`}>
                                ({subTag.count})
                              </span>
                            </button>
                          )}
                          {/* Edit/Delete buttons for sub-tag in edit mode */}
                          {tagEditMode && !(editingSubTag?.parent === parentTag.name && editingSubTag?.name === subTag.name) && (
                            <div className="absolute right-0.5 top-1/2 -translate-y-1/2 flex items-center space-x-0.5">
                              <button
                                onClick={() => {
                                  setEditingSubTag({ parent: parentTag.name, name: subTag.name });
                                  setEditSubTagName(subTag.name);
                                }}
                                className="w-4 h-4 rounded bg-blue-100 text-blue-600 hover:bg-blue-200 flex items-center justify-center"
                                title="Edit sub-tag"
                              >
                                <i className="ri-pencil-line text-[10px]"></i>
                              </button>
                              <button
                                onClick={() => handleDeleteSubTag(parentTag.name, subTag.name)}
                                className="w-4 h-4 rounded bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center"
                                title="Delete sub-tag"
                              >
                                <i className="ri-delete-bin-line text-[10px]"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Side - Articles List, Favorites, or Stats Table */}
          <div className={(viewMode === 'articles' || viewMode === 'favorites') ? 'col-span-9' : 'col-span-12'}>

            {/* Favorites View */}
            {viewMode === 'favorites' && (
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50/30 rounded-xl border border-amber-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-yellow-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <i className="ri-star-fill text-amber-500 text-2xl mr-3"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">My Favorites</h3>
                        <p className="text-sm text-gray-600">{favoriteItems.length} items saved</p>
                      </div>
                    </div>
                    {/* Article Type Filter Dropdown */}
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
                    >
                      <option value="all">All Types</option>
                      {apiArticleTypes.map((articleType) => (
                        <option key={articleType.dswiki_artcl_type_id} value={String(articleType.dswiki_artcl_type_id)}>
                          {articleType.artcl_type_nm}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="p-6">
                  {favoriteItems.filter(item => categoryFilter === 'all' || item.category === categoryFilter).length > 0 ? (
                    <div className="space-y-4">
                      {favoriteItems
                        .filter(item => categoryFilter === 'all' || item.category === categoryFilter)
                        .map(item => (
                          <div
                            key={item.id}
                            className="border-2 border-amber-200 rounded-lg p-4 bg-white hover:bg-amber-50 hover:border-amber-400 hover:shadow-lg transition-all duration-200 ease-in-out cursor-pointer"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start gap-3 flex-1">
                                {/* Category Badge */}
                                <span className={`px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1 border shrink-0 ${getCategoryColor(item.category)}`}>
                                  <i className={getCategoryIcon(item.category)}></i>
                                  {getCategoryLabel(item.category)}
                                </span>
                                <Link
                                  to={`/app/ds-wiki/articles/${item.id}`}
                                  className="text-lg font-semibold text-gray-900 hover:text-amber-600"
                                >
                                  {item.title}
                                  {item.categoryNumber && (
                                    <span className="ml-2 text-sm font-normal text-gray-500">({item.categoryNumber})</span>
                                  )}
                                </Link>
                              </div>
                              <button
                                className="text-amber-500 hover:text-amber-600 transition-colors"
                                title="Remove from favorites"
                              >
                                <i className="ri-star-fill text-xl"></i>
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{item.summary}</p>
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <div className="flex items-center space-x-4">
                                <span className="flex items-center">
                                  <i className="ri-user-line mr-1"></i>
                                  {item.author}
                                </span>
                                <span className="flex items-center">
                                  <i className="ri-calendar-line mr-1"></i>
                                  {new Date(item.lastUpdated).toLocaleDateString()}
                                </span>
                                <span className="flex items-center text-blue-600">
                                  <i className="ri-building-line mr-1"></i>
                                  {item.department}
                                </span>
                              </div>
                              <div className="flex items-center space-x-4">
                                <span className="flex items-center">
                                  <i className="ri-eye-line mr-1"></i>
                                  {item.views.toLocaleString()} views
                                </span>
                                <Link
                                  to={`/app/ds-wiki/articles/${item.id}`}
                                  className="text-amber-600 hover:text-amber-700 font-medium"
                                >
                                  Read More →
                                </Link>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <i className="ri-star-line text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No favorites found</h3>
                      <p className="text-gray-500 mb-4">
                        {categoryFilter !== 'all'
                          ? `You don't have any ${categoryFilter === 'go' ? 'Government Orders' : categoryFilter + 's'} in your favorites yet.`
                          : 'Start adding articles, circulars, and GOs to your favorites!'}
                      </p>
                      <button
                        onClick={() => setViewMode('articles')}
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                      >
                        Browse Articles
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Stats Views */}
            {viewMode === 'stats-department' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-building-line mr-2 text-blue-600"></i>
                      Statistics by Department
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Article counts and views across all departments</p>
                  </div>
                  <button
                    onClick={() => openCreateModal('department')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i>
                    Add Department
                  </button>
                </div>
                <div className="p-4">
                  <DataGrid
                    dataSource={statsByDepartment}
                    columns={departmentStatsColumns}
                    gridConfig={statsGridConfig}
                  />
                </div>
              </div>
            )}

            {viewMode === 'stats-location' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-teal-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-map-pin-line mr-2 text-green-600"></i>
                      Statistics by Location
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Article counts and views across all locations</p>
                  </div>
                  <button
                    onClick={() => openCreateModal('location')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i>
                    Add Location
                  </button>
                </div>
                <div className="p-4">
                  <DataGrid
                    dataSource={statsByLocation}
                    columns={locationStatsColumns}
                    gridConfig={statsGridConfig}
                  />
                </div>
              </div>
            )}

            {viewMode === 'stats-tags' && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <i className="ri-price-tag-3-line mr-2 text-purple-600"></i>
                      Statistics by Tags
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Article counts and views across all tags and sub-tags</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setCreateType('tag');
                        setNewItemName('');
                        setShowCreateModal(true);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      <i className="ri-add-line"></i>
                      Add Tag
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <DataGrid
                    dataSource={statsByTags}
                    columns={tagStatsColumns}
                    gridConfig={statsGridConfig}
                  />
                </div>
              </div>
            )}

            {/* Articles List View */}
            {viewMode === 'articles' && (
            <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-6">
                {filteredArticles.length > 0 ? (
                  <div className="space-y-4">
                    {filteredArticles.map(article => (
                      <div
                        key={article.id}
                        className="border-2 border-gray-200 rounded-lg p-4 bg-white hover:bg-blue-50 hover:border-blue-400 hover:shadow-lg transition-all duration-200 ease-in-out cursor-pointer"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3 flex-1">
                            {/* Category Badge */}
                            <span className={`px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1 border shrink-0 ${getCategoryColor(article.category)}`}>
                              <i className={getCategoryIcon(article.category)}></i>
                              {getCategoryLabel(article.category)}
                            </span>
                            <Link
                              to={`/app/ds-wiki/articles/${article.id}`}
                              className="text-lg font-semibold text-gray-900 hover:text-blue-600"
                            >
                              {article.title}
                              {article.categoryNumber && (
                                <span className="ml-2 text-sm font-normal text-gray-500">({article.categoryNumber})</span>
                              )}
                            </Link>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${getStatusColor(article.status)}`}>
                            {getStatusText(article.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{article.summary}</p>
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <i className="ri-user-line mr-1"></i>
                              {article.author}
                            </span>
                            <span className="flex items-center">
                              <i className="ri-calendar-line mr-1"></i>
                              {new Date(article.lastUpdated).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <i className="ri-eye-line mr-1"></i>
                              {article.views.toLocaleString()} views
                            </span>
                            <Link
                              to={`/app/ds-wiki/articles/${article.id}`}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              Read More →
                            </Link>
                          </div>
                        </div>
                        {/* Tags */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center flex-wrap gap-2">
                            {article.tags && article.tags.length > 0 ? (
                              article.tags.map(tag => (
                                <button
                                  key={tag}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleTag(tag);
                                  }}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-200 border ${
                                    selectedTags.includes(tag)
                                      ? 'bg-green-500 text-white border-green-500'
                                      : 'bg-gray-100 text-gray-600 border-transparent hover:bg-green-100 hover:border-green-400 hover:text-green-700'
                                  }`}
                                >
                                  #{tag}
                                </button>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400 italic">No tags</span>
                            )}
                          </div>
                          <button
                            onClick={() => openAssignTagsModal(article.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 flex items-center"
                            title="Manage article tags"
                          >
                            <i className="ri-price-tag-3-line mr-1"></i>
                            Edit Tags
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <i className="ri-article-line text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">No articles found</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Right Sidebar - Only show in articles or favorites view */}
          {(viewMode === 'articles' || viewMode === 'favorites') && (
          <div className="col-span-3 space-y-6">
            {/* My Articles */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <i className="ri-file-user-line mr-2 text-blue-600"></i>
                  My Articles
                </h3>
                {myArticlesFilter !== null && (
                  <button
                    onClick={() => setMyArticlesFilter(null)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* My Article Stats - Nothing selected by default */}
              <div className="space-y-2 mb-4">
                <button
                  onClick={() => setMyArticlesFilter(myArticlesFilter === 'all' ? null : 'all')}
                  className={`w-full flex justify-between items-center text-sm p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    myArticlesFilter === 'all'
                      ? 'bg-blue-50 border-blue-400 shadow-sm'
                      : 'border-transparent bg-white hover:bg-blue-50 hover:border-blue-300'
                  }`}
                >
                  <span className="text-gray-600 flex items-center">
                    <i className="ri-article-line mr-2 text-blue-500"></i>
                    Total
                  </span>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">{myArticlesStats.total}</span>
                    <i className="ri-arrow-right-s-line ml-1 text-gray-400"></i>
                  </div>
                </button>
                <button
                  onClick={() => setMyArticlesFilter(myArticlesFilter === 'published' ? null : 'published')}
                  className={`w-full flex justify-between items-center text-sm p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    myArticlesFilter === 'published'
                      ? 'bg-green-50 border-green-400 shadow-sm'
                      : 'border-transparent bg-white hover:bg-green-50 hover:border-green-300'
                  }`}
                >
                  <span className="text-gray-600 flex items-center">
                    <i className="ri-check-double-line mr-2 text-green-500"></i>
                    Published
                  </span>
                  <div className="flex items-center">
                    <span className="font-medium text-green-600">{myArticlesStats.published}</span>
                    <i className="ri-arrow-right-s-line ml-1 text-gray-400"></i>
                  </div>
                </button>
                <button
                  onClick={() => setMyArticlesFilter(myArticlesFilter === 'under_curation' ? null : 'under_curation')}
                  className={`w-full flex justify-between items-center text-sm p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    myArticlesFilter === 'under_curation'
                      ? 'bg-yellow-50 border-yellow-400 shadow-sm'
                      : 'border-transparent bg-white hover:bg-yellow-50 hover:border-yellow-300'
                  }`}
                >
                  <span className="text-gray-600 flex items-center">
                    <i className="ri-time-line mr-2 text-yellow-500"></i>
                    Under Review
                  </span>
                  <div className="flex items-center">
                    <span className="font-medium text-yellow-600">{myArticlesStats.underReview}</span>
                    <i className="ri-arrow-right-s-line ml-1 text-gray-400"></i>
                  </div>
                </button>
                <button
                  onClick={() => setMyArticlesFilter(myArticlesFilter === 'draft' ? null : 'draft')}
                  className={`w-full flex justify-between items-center text-sm p-2 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                    myArticlesFilter === 'draft'
                      ? 'bg-gray-100 border-gray-400 shadow-sm'
                      : 'border-transparent bg-white hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <span className="text-gray-600 flex items-center">
                    <i className="ri-draft-line mr-2 text-gray-500"></i>
                    Draft
                  </span>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-600">{myArticlesStats.draft}</span>
                    <i className="ri-arrow-right-s-line ml-1 text-gray-400"></i>
                  </div>
                </button>
              </div>

              {/* Contributors Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Contributors</span>
                </div>

                {/* Collapsed View - Show first 2 contributors */}
                {!showAllContributors ? (
                  <div>
                    <div className="flex items-center">
                      {/* Stacked Avatars */}
                      <div className="flex -space-x-2">
                        {contributors.slice(0, 2).map((c, idx) => (
                          <div
                            key={c.name}
                            className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold border-2 border-white"
                            title={c.name}
                          >
                            {c.avatar}
                          </div>
                        ))}
                        {(contributors.length > 2 || editors.length > 0) && (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-semibold border-2 border-white">
                            +{contributors.length - 2 + editors.length}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => setShowAllContributors(true)}
                        className="ml-3 text-sm text-blue-600 hover:text-blue-700"
                      >
                        {contributors.length > 2
                          ? `${contributors.slice(0, 2).map(c => c.name.split(' ')[0]).join(', ')} +${contributors.length - 2} contributors`
                          : contributors.map(c => c.name.split(' ')[0]).join(', ')
                        }
                        {editors.length > 0 && `, ${editors.length} editor${editors.length > 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Expanded View - Full List */
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Authors & Contributors</div>
                    {contributors.map((contributor) => (
                      <button
                        key={contributor.name}
                        onClick={() => console.log('View profile:', contributor.name)}
                        className="w-full flex items-center space-x-2 p-2 rounded-lg border-2 border-transparent bg-white hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                          {contributor.avatar}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-gray-900">{contributor.name}</p>
                          <p className="text-xs text-gray-500">{contributor.role}</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </button>
                    ))}

                    {editors.length > 0 && (
                      <>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-3 mb-2">Editors & Reviewers</div>
                        {editors.map((editor) => (
                          <button
                            key={editor.name}
                            onClick={() => console.log('View profile:', editor.name)}
                            className="w-full flex items-center space-x-2 p-2 rounded-lg border-2 border-transparent bg-white hover:bg-purple-50 hover:border-purple-300 transition-all duration-200 cursor-pointer"
                          >
                            <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                              {editor.avatar}
                            </div>
                            <div className="flex-1 text-left">
                              <p className="text-sm font-medium text-gray-900">{editor.name}</p>
                              <p className="text-xs text-gray-500">{editor.role}</p>
                            </div>
                            <i className="ri-arrow-right-s-line text-gray-400"></i>
                          </button>
                        ))}
                      </>
                    )}

                    <button
                      onClick={() => setShowAllContributors(false)}
                      className="text-sm text-gray-500 hover:text-gray-700 mt-2"
                    >
                      <i className="ri-arrow-up-s-line mr-1"></i>
                      Show less
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Top Contributors / Top Articles Toggle */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              {/* Toggle Button Group */}
              <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                <button
                  onClick={() => setRankingTab('contributors')}
                  className={`flex-1 px-2 py-2 text-xs font-medium rounded-md transition-all ${
                    rankingTab === 'contributors'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-user-star-line mr-1"></i>
                  Top Contributors
                </button>
                <button
                  onClick={() => setRankingTab('articles')}
                  className={`flex-1 px-2 py-2 text-xs font-medium rounded-md transition-all ${
                    rankingTab === 'articles'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-fire-line mr-1"></i>
                  Top Articles
                </button>
              </div>

              {rankingTab === 'contributors' ? (
                <div className="space-y-2">
                  {topContributorsList.length > 0 ? (
                    topContributorsList.map((contributor, index) => (
                      <button
                        key={`${contributor.name}-${index}`}
                        onClick={() => console.log('View contributor:', contributor.name)}
                        className="w-full flex items-center space-x-3 p-2 rounded-lg border-2 border-transparent bg-white hover:bg-yellow-50 hover:border-yellow-300 transition-all duration-200 cursor-pointer"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-400'
                        }`}>
                          {contributor.avatar}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-sm font-medium text-gray-900">{contributor.name}</p>
                          <p className="text-xs text-gray-500">{contributor.articles} articles</p>
                        </div>
                        <span className="text-xs font-medium text-gray-400">#{index + 1}</span>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No contributors yet</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {topArticles.length > 0 ? (
                    topArticles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/app/ds-wiki/articles/${article.id}`}
                        className="flex items-center space-x-3 p-2 rounded-lg border-2 border-transparent bg-white hover:bg-red-50 hover:border-red-300 transition-all duration-200"
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
                          index === 0 ? 'bg-red-500' : index === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}>
                          <i className="ri-fire-fill text-xs"></i>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 line-clamp-1">{article.title}</p>
                          <p className="text-xs text-gray-500">{article.views.toLocaleString()} views</p>
                        </div>
                        <span className="text-xs font-medium text-gray-400">#{index + 1}</span>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </Link>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No top articles yet</p>
                  )}
                </div>
              )}
            </div>

            {/* Department Curators */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <i className="ri-shield-user-line mr-2 text-purple-600"></i>
                Department Curators
              </h4>
              <div className="space-y-3">
                {departmentCuratorsList.length > 0 ? (
                  departmentCuratorsList.map((curator, index) => (
                    <button
                      key={`${curator.name}-${index}`}
                      onClick={() => console.log('View curator:', curator.name)}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg border-2 border-transparent bg-purple-50 hover:bg-purple-100 hover:border-purple-300 transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-semibold">
                        {curator.avatar}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-gray-900">{curator.name}</p>
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>{curator.role}</span>
                          <span className="text-purple-600 font-medium">{curator.articles} articles curated</span>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400"></i>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No curators assigned</p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Assign Tags Modal */}
      {showAssignTagsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="ri-price-tag-3-line mr-2 text-green-600"></i>
                Assign Tags to Article
              </h3>
              <button
                onClick={() => {
                  setShowAssignTagsModal(false);
                  setSelectedArticleForTags(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Select tags to assign to this article. Click on a tag to toggle selection.
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {tags.map(tag => {
                  const article = filteredArticles.find(a => a.id === selectedArticleForTags);
                  const isAssigned = article?.tags?.includes(tag.name) || false;
                  return (
                    <button
                      key={tag.name}
                      onClick={() => handleAssignTag(tag.name)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isAssigned
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      #{tag.name}
                      {isAssigned && <i className="ri-check-line ml-1"></i>}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <i className="ri-add-line text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Add new tag..."
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.target as HTMLInputElement).value.trim()) {
                      const newTag = (e.target as HTMLInputElement).value.trim();
                      handleAssignTag(newTag);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setShowAssignTagsModal(false);
                  setSelectedArticleForTags(null);
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  alert('Tags saved successfully!');
                  setShowAssignTagsModal(false);
                  setSelectedArticleForTags(null);
                }}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && selectedConfigItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className={`mr-2 ${
                  configType === 'department' ? 'ri-building-line text-blue-600' :
                  configType === 'location' ? 'ri-map-pin-line text-green-600' :
                  'ri-price-tag-3-line text-purple-600'
                }`}></i>
                Configure {configType === 'department' ? 'Department' : configType === 'location' ? 'Location' : 'Tag'}
              </h3>
              <button
                onClick={() => setShowConfigModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Name Edit Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {configType === 'department' ? 'Department' : configType === 'location' ? 'Location' : 'Tag'} Name
                </label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleSaveEdit}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                    >
                      <i className="ri-check-line"></i>
                    </button>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-900 font-medium">{editName}</span>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                    >
                      <i className="ri-edit-line mr-1"></i>
                      Edit
                    </button>
                  </div>
                )}
              </div>

              {/* Curators Section - Only for Department */}
              {configType === 'department' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Department Curators</label>
                    <button className="text-blue-600 hover:text-blue-700 text-sm flex items-center">
                      <i className="ri-add-line mr-1"></i>
                      Add Curator
                    </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {configCuratorsLoading ? (
                      <ListSkeleton rows={2} />
                    ) : configCurators.length > 0 ? (
                      configCurators.map((curator: any, idx: number) => (
                        <div key={curator.dswiki_crtr_id || idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white text-sm font-semibold">
                              {(curator.crtr_nm || curator.usr_nm || 'C').charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{curator.crtr_nm || curator.usr_nm || 'Curator'}</p>
                              <p className="text-xs text-gray-500">{curator.is_snr_in ? 'Senior Curator' : 'Curator'}</p>
                            </div>
                          </div>
                          <button className="text-red-500 hover:text-red-700">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <i className="ri-user-star-line text-2xl text-gray-300 mb-2"></i>
                        <p className="text-sm text-gray-500">No curators assigned yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Approvers Section - Only for Department */}
              {configType === 'department' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Department Approvers</label>
                    <button className="text-blue-600 hover:text-blue-700 text-sm flex items-center">
                      <i className="ri-add-line mr-1"></i>
                      Add Approver
                    </button>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <i className="ri-shield-user-line text-2xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-500">No approvers assigned yet</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Summary */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Statistics</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{selectedConfigItem.total || 0}</p>
                    <p className="text-xs text-gray-500">Total Articles</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{selectedConfigItem.published || 0}</p>
                    <p className="text-xs text-gray-500">Published</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{(selectedConfigItem.views || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">Total Views</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  alert('Configuration saved successfully!');
                  setShowConfigModal(false);
                }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className={`mr-2 ${
                  createType === 'department' ? 'ri-building-line text-blue-600' :
                  createType === 'location' ? 'ri-map-pin-line text-green-600' :
                  createType === 'tag' ? 'ri-price-tag-3-line text-purple-600' :
                  'ri-price-tag-3-line text-purple-400'
                }`}></i>
                Create New {
                  createType === 'department' ? 'Department' :
                  createType === 'location' ? 'Location' :
                  createType === 'tag' ? 'Tag' : 'Sub-Tag'
                }
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Parent Tag Selector for Sub-Tag */}
              {createType === 'subtag' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parent Tag
                  </label>
                  <select
                    value={parentTagForSubtag}
                    onChange={(e) => setParentTagForSubtag(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a parent tag...</option>
                    {statsByTags.filter(t => t.isParent).map(t => (
                      <option key={t.tag} value={t.tag}>{t.tag}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {createType === 'department' ? 'Department' :
                   createType === 'location' ? 'Location' :
                   createType === 'tag' ? 'Tag' : 'Sub-Tag'} Name
                </label>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder={`Enter ${
                    createType === 'department' ? 'department' :
                    createType === 'location' ? 'location' :
                    createType === 'tag' ? 'tag' : 'sub-tag'
                  } name...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateItem}
                disabled={!newItemName.trim() || (createType === 'subtag' && !parentTagForSubtag)}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                  createType === 'department' ? 'bg-blue-600 hover:bg-blue-700' :
                  createType === 'location' ? 'bg-green-600 hover:bg-green-700' :
                  'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
